<template>
  <WithLoader :isLoading="isLoading" classType="primary">
    <div class="history-table__wrapper" :class="{ [extraClass]: !!extraClass }">
      <VDataTable
        :currentSort="sorting"
        :headers="tableFields.headers"
        :rows="tableFields.rows"
        extraClass="history-table"
        @sort="onSort"
        classType="primary"
      >
      </VDataTable>
    </div>
  </WithLoader>
</template>

<script>
import Vue from "vue";
import VueTypes from "vue-types";
import { mapActions, mapGetters } from "vuex";
import moment from "moment";

// components
import VDataTable from "@/components/VDataTable";
import VTextTooltiped from "@/components/VTextTooltiped";
import VPreviewImage from "@/components/VPreviewImage";
import VSwitcher from "@/components/VSwitcher";

// mixins
import withNavigate from "@/mixins/withNavigate";

import {
  getFormattedDate,
  getPostcardFullType,
  capitalizeFirstLetter,
  processMailpieceSource
} from "@/utils/util";

import { ActivityTrackingLabelsMap } from "@/constants/common";
import { formatMomentDT } from "@/utils/util";
import { fetchMailPieceThumbnail } from "@/api-client/contacts";

// utils
import { getLetterType } from "@/utils/util";

//images
import postcardIcon from "@/assets/images/postcard-icon-light.svg";
import postcardIconActive from "@/assets/images/postcard-icon-active.svg";
import letterIcon from "@/assets/images/letter-icon.svg";
import letterIconActive from "@/assets/images/letter-icon-active.svg";

export default Vue.component("LettersHistoryTable", {
  props: {
    items: VueTypes.arrayOf(
      VueTypes.shape({
        id: VueTypes.string,
        name: VueTypes.string,
        first_name: VueTypes.string,
        last_name: VueTypes.string,
        template_type: VueTypes.string,
        created: VueTypes.oneOfType([
          VueTypes.instanceOf(Date),
          VueTypes.string
        ]),
        edited: VueTypes.oneOfType([
          VueTypes.instanceOf(Date),
          VueTypes.string
        ]),
        type: VueTypes.string,
        size: VueTypes.string,
        preview: VueTypes.string,
        url_to_file: VueTypes.string
      })
    ),
    noMoreData: VueTypes.bool.def(false),
    hasNextPage: VueTypes.bool.def(false)
  },

  mixins: [withNavigate],

  components: {
    VDataTable
  },

  data() {
    return {
      dataKey: "letters"
    };
  },

  computed: {
    tableFields() {
      return {
        headers: [
          {
            key: "name",
            label: "Template/Name",
            width: "30%",
            sortable: true,
            sortType: "string"
          },
          {
            key: "type",
            label: "Type",
            sortable: true,
            width: "15%"
          },
          {
            key: "status",
            label: "Status",
            sortable: true,
            width: "15%"
          },
          {
            key: "source",
            label: "Source",
            sortable: true,
            width: "16%"
          },
          {
            key: "created_at",
            label: "Created",
            sortable: true,
            width: "20%"
          },
          {
            key: "element-switch",
            label: "",
            component: VSwitcher,
            display: true,
            props: {
              type: "element",
              activeKey: this.dataKey,
              switcherKeys: {
                left: {
                  key: "postcards",
                  label: "",
                  icon: postcardIcon,
                  iconActive: postcardIconActive
                },

                right: {
                  key: "letters",
                  label: "",
                  icon: letterIcon,
                  iconActive: letterIconActive
                }
              },
              onClickHandler: this._updateLayout
            },
            width: "7%"
          }
        ],

        rows: this.items.map(mailpiece => ({
          key: mailpiece.id,
          values: [
            {
              key: "preview",
              component: VPreviewImage,
              props: {
                image: mailpiece.thumbnail_url,
                text: mailpiece.name
                  .split(" ")
                  .map(item => `${item[0]}${item.toLowerCase().slice(1)}`)
                  .join(" "),
                routerLink: `/contacts/${mailpiece.contact_id}?o=letters`,
                withPdfDownload: !!mailpiece.url_to_file || !!mailpiece.pdf_url,
                hideTitle: true,
                pdfDownloadHandler: e => this._downloadPdf(e, mailpiece),
                pdfLoading: this.downloadingPdfId === mailpiece.id,
                orientation: "vertical",
                extraClass: "history-table-preview-vertical"
              },
              extraClass: "history-preview"
            },
            {
              key: "type",
              value: getLetterType(mailpiece.color, mailpiece.mail_type)
            },
            {
              key: "latest_status",
              component: VTextTooltiped,
              props: {
                text: mailpiece.latest_status
                  ? ActivityTrackingLabelsMap[mailpiece.latest_status]
                  : "Processed",
                tooltipText: mailpiece.latest_status
                  ? this._getFormattedDate(mailpiece[mailpiece.latest_status])
                  : this._getFormattedDate(mailpiece.created_at),
                onClick: e =>
                  this._onStatusClick(e, mailpiece.contact_id, mailpiece.id),
                noPadding: false
              }
            },
            {
              key: "source",
              // value: processMailpieceSource(mailpiece.source),
              value: "Webhook",
              component:
                mailpiece.source === "campaign" ? VTextTooltiped : null,
              props: {
                tooltipText: mailpiece.campaign_name,
                text: "Campaign",
                onClick: () =>
                  this.navigate(`/campaigns/${mailpiece.campaign_id}`)
              }
            },
            {
              key: "created_at",
              value: this._getFormattedDate(mailpiece.created_at)
            },

            {
              key: "",
              value: ""
            }
          ]
        }))
      };
    },

    ...mapGetters("account", ["getAccount", "getUser"]),
    ...mapGetters("letters", [
      "currentPage",
      "sorting",
      "searchString",
      "isLoading"
    ]),
    ...mapGetters("service", ["itemsPerPage"])
  },

  methods: {
    _updateLayout(active) {
      this.dataKey = active;
      localStorage.setItem("mailpieceOption", this.dataKey);
      if (this.dataKey !== "letters") {
        this.navigate("/history/postcards");
      }
    },

    async _downloadPdf(e, { url_to_file, pdf_url }) {
      e.stopPropagation();

      const file_to_open = url_to_file || pdf_url;

      if (file_to_open) {
        return window.open(file_to_open, "_blank");
      }

      return;
    },

    formatDate(date) {
      return getFormattedDate(date);
    },

    _getFormattedDate(date) {
      return moment(date).format("MMM D, YYYY [at] h:mm:ss A z");
    },

    onSort(sorting) {
      this.fetchAccountLettersHistory({
        page: this.currentPage,
        searchString: this.searchString,
        sorting: sorting,
        limit: this.itemsPerPage
      });

      this.changeSort(sorting);
    },

    getSize(type) {
      return getPostcardFullType(type);
    },

    _onStatusClick(e, contactId, mailPieceId) {
      e.stopPropagation();
      this.navigate(`/contacts/${contactId}?e=${mailPieceId}`);
    },

    ...mapActions("letters", ["changeSort", "fetchAccountLettersHistory"])
  },

  destroyed() {
    this.changeSort({ key: "edited_at", value: true });
  }
});
</script>

<style lang="scss">
.history-table__wrapper /deep/ {
  table {
    table-layout: fixed;

    .history-table-preview-vertical {
      .title.with-download {
        .ball {
          transform: translate(-5%, -13%);
        }

        img {
          bottom: 3px;
          right: 3px;
        }
      }

      .preview-text {
        a {
          display: block;
          white-space: nowrap;
          width: 85%;
          overflow: hidden;
          text-overflow: ellipsis;
        }
      }
    }
  }
}
</style>
